/**
 * Shared cookie helpers for team-battle lobby preferences.
 * These are written by Preferences.tsx (inside the lobby) and read by
 * ActionButtons.tsx + CreateLobby.tsx (at join / create time) so the correct
 * surrenderHP / readyHP / strategy values are always sent to the backend on
 * the very first join, not just after the user edits them in the lobby.
 *
 * Cookie key format:  prefs_<mercenaryId>_<battleType>
 * battleType examples: 'normal', 'death', 'pve'
 */

export interface LobbyPreferencesData {
  surrenderHP: number; // fractional 0–1
  readyHP: number;     // fractional 0–1
  strategy: string;
}

const buildKey = (mercenaryId: number, battleType: string) =>
  `prefs_${mercenaryId}_${battleType}`;

export const loadCookiePrefs = (
  mercenaryId: number,
  battleType: string,
): Partial<LobbyPreferencesData> | null => {
  const key = buildKey(mercenaryId, battleType);
  const entry = document.cookie
    .split('; ')
    .find((r) => r.startsWith(key + '='));
  if (!entry) return null;
  try {
    return JSON.parse(decodeURIComponent(entry.split('=').slice(1).join('=')));
  } catch {
    return null;
  }
};

export const saveCookiePrefs = (
  mercenaryId: number,
  battleType: string,
  prefs: LobbyPreferencesData,
): void => {
  const key = buildKey(mercenaryId, battleType);
  const value = encodeURIComponent(JSON.stringify(prefs));
  const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
  const secureAttr = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${key}=${value}; expires=${expires}; path=/; SameSite=Lax${secureAttr}`;
};
